import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { CommonIcons } from 'common-src/assets/Icons';
import { Button, Checkbox, RequestHandlerScreen, Select } from 'common-src/components/base';
import { isFeatureEnabled } from 'common-src/models/FeatureFlag';
import Patient, {
  HasPcg,
  HasPcgOptions,
  sendScreeningLink,
  updatePatient,
} from 'common-src/models/Patient';
import {
  deletePatientCaregiver,
  getPatientCaregivers,
  getPatientCaregiversRequest,
  updatePatientCaregiver,
} from 'common-src/models/PatientCaregiver';
import { getFormattedPhoneNumber } from 'common-src/models/PatientPhoneNumber/helpers';
import { Colors, ColorsNew } from 'common-src/styles';

import { ModalType } from 'src/components/base/ModalGroup';
import { ActionsRow, BaseTable, TablePagination, TextItem } from 'src/components/table';
import { ResultsCount } from 'src/components/table/TablePagination/constants';
import { TabsViewNew } from 'src/components/tabs';
import { openModal } from 'src/features/modals';
import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';
import useInterval from 'src/hooks/useInterval';
import useRequestLoading from 'src/hooks/useRequestLoading';

import styles from './Caregivers.module.scss';
import { CaregiversTabs, DefaultTabInfo, TableHeaders } from './constants';
import { getTabInfo } from './helpers';

const Caregivers = ({
  patient: { id: patientId, hasPcg, caregiverUnavailableForAllAppointments, isGuide },
}) => {
  const dispatch = useDispatch();

  const tabs = Object.keys(CaregiversTabs);

  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const [filteredCaregivers, setFilteredCaregivers] = useState([]);
  const [tabInfo, setTabInfo] = useState(DefaultTabInfo);
  const [paginationData, setPaginationData] = useState({ from: 0, to: undefined });
  const [isLoading, setIsLoading] = useState(false);
  const [caregiverUnavailable, setCaregiverUnavailable] = useState(
    Boolean(caregiverUnavailableForAllAppointments),
  );

  const patientCaregivers = useCustomSelector((state) => getPatientCaregivers(state, patientId));
  const hasActiveCaregivers = Boolean(
    patientCaregivers.filter((caregiver) => !caregiver.deletedAt).length,
  );

  const { showAlert, AlertType } = useAlert();
  const { isRequesting } = useRequestLoading([getPatientCaregiversRequest({ patientId })]);
  const isCdrCaregiverWFEnabled = useCustomSelector((state) =>
    isFeatureEnabled(state, 'cdrCaregiverWF'),
  );

  useInterval(() => {
    dispatch(getPatientCaregiversRequest({ patientId, sort_order: 'desc', limit: 5 }, {}));
  });

  useEffect(() => {
    const { tabInfo, activeCaregivers, inactiveCaregivers, allCaregivers } =
      getTabInfo(patientCaregivers);
    setTabInfo(tabInfo);

    switch (selectedTab) {
      case tabs[0]:
        setFilteredCaregivers(activeCaregivers);
        break;
      case tabs[1]:
        setFilteredCaregivers(inactiveCaregivers);
        break;
      default:
        setFilteredCaregivers(allCaregivers);
        break;
    }
  }, [selectedTab, patientCaregivers]);

  const getData = () =>
    filteredCaregivers.map((caregiver) => ({
      firstName: (
        <TextItem
          isActive
          text={caregiver.firstName}
          isUnderlined
          onClick={() => dispatch(openModal(ModalType.CAREGIVER_DETAILS, { caregiver, patientId }))}
        />
      ),
      lastName: <TextItem isActive text={caregiver.lastName} />,
      isPrimary: (
        <TextItem isActive text={caregiver.isPrimary ? 'Yes' : 'No'} value={caregiver.isPrimary} />
      ),
      phone: (
        <TextItem
          isActive
          text={
            caregiver.phone
              ? getFormattedPhoneNumber(
                  caregiver.phone?.length > 10 ? caregiver.phone.substring(2) : caregiver.phone,
                )
              : '-'
          }
        />
      ),
      dateOfBirth: (
        <TextItem
          isActive
          text={caregiver.dateOfBirth ? moment(caregiver.dateOfBirth).format('MM/DD/YYYY') : '-'}
          value={caregiver.dateOfBirth}
        />
      ),
      relationship: <TextItem isActive text={caregiver.relationship} />,
      medicareStatus: <TextItem isActive text={caregiver.medicareStatus} />,
      actions: (
        <ActionsRow
          options={[
            {
              id: 'edit-button',
              text: 'Edit',
              backgroundColor: '#555671B2',
              color: 'white',
              disabled: caregiver.deletedAt,
              onClick: () =>
                dispatch(
                  openModal(ModalType.CAREGIVER, {
                    patientId,
                    caregiver,
                    hasActiveCaregivers,
                    isGuide,
                  }),
                ),
            },
            {
              id: 'set-primary-button',
              text: 'Set as primary',
              backgroundColor: '#375844CC',
              color: 'white',
              disabled: caregiver.isPrimary || caregiver.deletedAt,
              onClick: () =>
                dispatch(
                  updatePatientCaregiver(
                    { id: caregiver.id, isPrimary: true },
                    {
                      successBlock: () => {
                        showAlert(AlertType.Success, '', 'Caregiver set as primary!');
                      },
                      errorBlock: (err) => showAlert(AlertType.Error, '', err),
                    },
                  ),
                ),
            },
            {
              id: 'send-link-button',
              text: 'Send ZBI-22 link',
              backgroundColor: ColorsNew.baseGreen,
              color: 'white',
              disabled: caregiver.deletedAt || !caregiver.isPrimary,
              onClick: () =>
                dispatch(
                  openModal(ModalType.SEND_SCREENER_LINK, {
                    patientId,
                    caregiver,
                    onSubmit: (channel, successCallback, errorCallback) =>
                      dispatch(
                        sendScreeningLink(
                          patientId,
                          { zbi22: true, channel, caregiverId: caregiver.id },
                          {
                            successBlock: () => {
                              showAlert(
                                AlertType.Success,
                                '',
                                'Successfully sent link for ZBI-22 Screening',
                              );
                              successCallback();
                            },
                            errorBlock: () => {
                              showAlert(
                                AlertType.Error,
                                '',
                                'Failed to send link for ZBI-22 Screening',
                              );
                              errorCallback();
                            },
                          },
                        ),
                      ),
                  }),
                ),
            },
            {
              id: 'mark-as-inactive-button',
              text: 'Mark as inactive',
              backgroundColor: '#F01F51B2',
              color: 'white',
              disabled: caregiver.deletedAt,
              showWarning: true,
              warningMessage: caregiver.isPrimary
                ? 'Are you sure you want to remove the primary caregiver?\nPlease assign a new primary care giver.'
                : null,
              onClick: () =>
                dispatch(
                  deletePatientCaregiver(caregiver.id, {
                    successBlock: () => {
                      showAlert(AlertType.Success, '', 'Caregiver deleted!');
                    },
                    errorBlock: (err) => showAlert(AlertType.Error, '', err),
                  }),
                ),
            },
          ]}
        />
      ),
    }));

  const renderRightContent = () => (
    <div
      className={[styles.rightContent, 'flex-row', 'flex-1', 'gap-20', 'm-b-10', 'm-l-10'].join(
        ' ',
      )}
    >
      <div className={['flex-row', 'vertically-centered', 'gap-10'].join(' ')}>
        <p className={[styles.label, 'font-s-14', 'font-w-500'].join(' ')}>Has PCG?</p>
        <Select
          id="has-pcg-select"
          classNames={[styles.select]}
          placeholder="Select"
          options={HasPcgOptions}
          onChange={(option) => {
            setIsLoading(true);
            dispatch(
              updatePatient(
                patientId,
                { hasPcg: option.value },
                {
                  successBlock: () => {
                    showAlert(
                      AlertType.Success,
                      '',
                      'You have successfully updated patient pcg status.',
                    );
                    setIsLoading(false);
                  },
                  errorBlock: (err) => {
                    showAlert(AlertType.Error, '', err);
                    setIsLoading(false);
                  },
                },
              ),
            );
          }}
          value={HasPcgOptions.find((op) => op.value === hasPcg)}
          size="small"
          withError={false}
          isLoading={isLoading}
        />
      </div>
      {isCdrCaregiverWFEnabled && (
        <div
          className={[styles.availabilityBanner, 'flex-row', 'vertically-centered', 'gap-10'].join(
            ' ',
          )}
        >
          <span className={[styles.availabilityTitle, 'font-s-14', 'font-w-600'].join(' ')}>
            Caregiver Availability
          </span>
          <Checkbox
            id="no-caregiver-available-checkbox"
            label="No caregiver available for any appointments"
            checked={caregiverUnavailable}
            onChange={(checked) => {
              setIsLoading(true);
              dispatch(
                updatePatient(
                  patientId,
                  { caregiverUnavailableForAllAppointments: checked },
                  {
                    successBlock: () => {
                      setCaregiverUnavailable(checked);
                      showAlert(AlertType.Success, '', 'Caregiver availability updated.');
                      setIsLoading(false);
                    },
                    errorBlock: (err) => {
                      showAlert(AlertType.Error, '', err);
                      setIsLoading(false);
                    },
                  },
                ),
              );
            }}
          />
        </div>
      )}
      <Button
        id="add-caregiver-button"
        text="Add Caregiver"
        onClick={() =>
          dispatch(openModal(ModalType.CAREGIVER, { patientId, hasActiveCaregivers, isGuide }))
        }
        backgroundColor={Colors.white}
        textColor={ColorsNew.darkGreen}
        iconSrc={CommonIcons.plusIcon}
        classNames={[styles.button]}
        disabled={
          [HasPcg.No, HasPcg.Undetermined].includes(hasPcg) ||
          (hasPcg === HasPcg.YesOne && hasActiveCaregivers)
        }
      />
    </div>
  );

  if (isRequesting) {
    return <RequestHandlerScreen isRequesting requestingText="Pulling caregivers..." />;
  }

  return (
    <>
      <TabsViewNew
        classNames={['flex-1 medical-tabs']}
        tabClassNames={['flex-none']}
        options={tabInfo}
        onTabSelected={setSelectedTab}
        showZero
        rightContent={renderRightContent()}
      />
      <BaseTable
        headers={TableHeaders}
        data={getData()}
        name="sdoh"
        classNames={[
          'medical-table-new',
          filteredCaregivers?.length <= ResultsCount[25] ? 'no-pagination' : '',
        ]}
        emptyText="No caregivers"
        emptyClassNames={['medical-table-empty']}
        initialSortColumn={{
          column: 'isPrimary',
          label: 'Is Primary?',
          width: '10%',
          sortBy: 'value',
        }}
        paginationData={paginationData}
      />
      <TablePagination onChangePageHandler={setPaginationData} results={filteredCaregivers} />
    </>
  );
};

Caregivers.propTypes = {
  patient: PropTypes.exact(Patient.schema),
};

export default Caregivers;
